import React, { useState, useEffect } from "react";
import {
  Container,
  Grid,
  Box,
  Avatar,
  Typography,
  IconButton,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import InsertEmoticonIcon from "@mui/icons-material/InsertEmoticon";
import DeleteIcon from "@mui/icons-material/Delete"; // Import the delete icon
import { db } from "../../../FirebaseConfig";
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  deleteDoc,
  getDoc,
} from "firebase/firestore";
import { useParams } from "react-router-dom";

const ShowIssuesComment = () => {
  const { issueId } = useParams();
  const [comments, setComments] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const [selectedCommentId, setSelectedCommentId] = useState(null);
  const [tempSwitchState, setTempSwitchState] = useState(null);

  const fetchComments = async (issueId) => {
    const comments_Ref = collection(db, "comments");
    const querySnapshot = await getDocs(comments_Ref);
    let all_comment_filter = [];

    querySnapshot.forEach((doc) => {
      const all_comment_Data = doc.data();

      if (all_comment_Data.issueId === issueId) {
        all_comment_filter.push({
          id: doc.id,
          ...all_comment_Data,
        });
      }
    });

    return all_comment_filter;
  };

  useEffect(() => {
    const loadComments = async () => {
      try {
        const new_loaded_Comment = await fetchComments(issueId);
        setComments(new_loaded_Comment);
      } catch (error) {
        console.error("Error fetching comments: ", error);
      }
    };

    if (issueId) {
      loadComments();
    }
  }, [issueId]);

  const handleSwitchChange = (commentId, currentValue) => {
    const newValue = !currentValue;
    setTempSwitchState(newValue);
    setDialogMessage(
      newValue ? "Accept this comment?" : "Reject this comment?"
    );
    setSelectedCommentId(commentId);
    setOpenDialog(true);
  };

  const handleDialogClose = async (confirmed) => {
    if (confirmed && selectedCommentId) {
      try {
        const commentDoc = doc(db, "comments", selectedCommentId);
        await updateDoc(commentDoc, { isCommentDetails: tempSwitchState });

        setComments((prevComments) =>
          prevComments.map((comment) =>
            comment.id === selectedCommentId
              ? { ...comment, isCommentDetails: tempSwitchState }
              : comment
          )
        );
      } catch (error) {
        console.error("Error updating comment: ", error);
      }
    }
    setOpenDialog(false);
  };

  const handleDeleteComment = async (commentId) => {
    try {
      const parent_Doc_Ref = doc(db, "Issues", issueId);
      const parentDocSnapshot = await getDoc(parent_Doc_Ref);

      if (parentDocSnapshot.exists()) {
        const parentData = parentDocSnapshot.data();

        const current_Comment_data_Count = parentData.comments || 0;

        const updatedCommentIds = parentData.comment_id.filter(
          (id) => id !== commentId
        );

        const updated_Comment_data_Count =
          current_Comment_data_Count > 0 ? current_Comment_data_Count - 1 : 0;

        await updateDoc(parent_Doc_Ref, {
          comment_id: updatedCommentIds,
          comments: updated_Comment_data_Count,
        });

        const commentDocRef = doc(db, "comments", commentId);
        await deleteDoc(commentDocRef);

        setComments((prevComments) =>
          prevComments.filter((comment) => comment.id !== commentId)
        );
        // setCommentCount(updated_Comment_data_Count);
      } else {
        console.error("Parent document does not exist");
      }
    } catch (error) {
      console.error("Error deleting comment: ", error);
    }
  };

  const CommentCard = ({
    avatarSrc,
    username,
    role,
    comment,
    date,
    isCommentDetails,
    commentId,
  }) => {
    const [isChecked, setIsChecked] = useState(isCommentDetails);

    const handleLocalSwitchChange = () => {
      handleSwitchChange(commentId, isChecked);
    };

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          mb: 3,
          height: "auto",
          padding: 2,
          backgroundColor: "#fff",
          borderRadius: 2,
          border: "1px solid black",
        }}
      >
        <Avatar src={avatarSrc} sx={{ mr: 2 }} />
        <div
          style={{
            flexGrow: 1,
            backgroundColor: "#fff",
            borderRadius: 2,
            padding: 2,
            padding: "0px",
            marginBottom: "0px",
          }}
        >
          <div>
            <Box
              display="flex"
              alignItems="center"
              sx={{ marginBottom: "0px" }}
            >
              <Box flexGrow={1}>
                <Typography variant="subtitle2">
                  <strong>{username}</strong> commented {date}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {role}
                </Typography>
              </Box>
              <Switch
                checked={isChecked}
                onChange={handleLocalSwitchChange}
                inputProps={{ "aria-label": "controlled" }}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "#000",
                  },
                }}
              />
              <IconButton onClick={() => handleDeleteComment(commentId)}>
                <DeleteIcon />
              </IconButton>
            </Box>

            <Box mt={1}>
              <Typography variant="body2">{comment}</Typography>
            </Box>

            <Box display="flex" alignItems="center" mt={1}>
              <IconButton>
                <InsertEmoticonIcon />
              </IconButton>
            </Box>
          </div>
        </div>

        <Dialog
          open={openDialog}
          onClose={() => handleDialogClose(false)}
          BackdropProps={{
            style: { backgroundColor: "rgba(0, 0, 0, 0.5)" },
          }}
          sx={{
            "& .MuiPaper-root": {
              boxShadow: "none",
            },
          }}
        >
          <Box
            sx={{
              width: "400px",
              padding: 2,
              backgroundColor: "white",
            }}
          >
            <DialogTitle>Confirmation</DialogTitle>
            <DialogContent>{dialogMessage}</DialogContent>
            <DialogActions>
              <Button onClick={() => handleDialogClose(false)}>Cancel</Button>
              <Button onClick={() => handleDialogClose(true)} autoFocus>
                Confirm
              </Button>
            </DialogActions>
          </Box>
        </Dialog>
      </Box>
    );
  };

  return (
    <Container
      maxWidth="lg"
      sx={{ mt: 2, justifyContent: "center", display: "flex" }}
    >
      <Grid
        container
        spacing={4}
        sx={{ display: "flex", justifyContent: "center" }}
      >
        <Grid item xs={12} md={8}>
          <Grid container spacing={2}>
            {comments.map((comment, index) => (
              <Grid item xs={12} key={index}>
                <CommentCard
                  avatarSrc={comment.avatarSrc || ""}
                  username={comment.username}
                  role={comment.role}
                  comment={comment.description}
                  date={comment.date}
                  isCommentDetails={comment.isCommentDetails}
                  commentId={comment.id}
                />
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ShowIssuesComment;
